import { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PortfolioFilters } from '@/components/PortfolioFilters';
import { PortfolioSearch } from '@/components/PortfolioSearch';
import { PortfolioSort } from '@/components/PortfolioSort';
import { projects } from '@/data/portfolio';
import { FeaturedProjects } from '@/components/FeaturedProjects';
import { CaseStudyForm } from '@/components/CaseStudyForm';
import { TestimonialsSection } from '@/components/TestimonialsSection';
import SEO from "@/components/SEO"; 
import WebDesignLocations from '@/components/WebDesignLocations';

type SortOption = 'newest' | 'oldest' | 'alphabetical';

export default function Portfolio() {
  const [filters, setFilters] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('newest');

  const filteredProjects = projects
    .filter(p => 
      filters.length === 0 || 
      p.categories.some(c => filters.includes(c))
    )
    .filter(p => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        p.name.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query) ||
        p.technologies?.some(t => t.toLowerCase().includes(query)) ||
        p.client?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      switch(sortOption) {
        case 'newest': return (b.year || 0) - (a.year || 0);
        case 'oldest': return (a.year || 0) - (b.year || 0);
        case 'alphabetical': return a.name.localeCompare(b.name);
        default: return 0;
      }
    });

  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const handleMove = (e: MouseEvent) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      container.style.setProperty('--mouse-x', x.toString());
      container.style.setProperty('--mouse-y', y.toString());
    };
    
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  return (
    <div className="bg-white text-black">
      <SEO
        title="Our Portfolio | Custom Software Development | First Soft UAE"
        description="Explore our portfolio of successful software development projects including web applications, mobile apps, and enterprise solutions."
        keywords="software portfolio, web app examples, mobile app case studies, UAE tech projects"
      />

      <div 
        ref={containerRef}
        className="relative overflow-hidden"
      >
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(56,182,255,0.1)_0%,_transparent_70%)] animate-pulse-slow" />
        </div>
        <div 
          className="container mx-auto px-4 pb-12 pt-24 max-w-7xl relative overflow-hidden"
          style={{
            '--mouse-x': '0.5',
            '--mouse-y': '0.5',
          } as React.CSSProperties}
        >
          <div className="absolute inset-0 -z-10 opacity-5">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_calc(var(--mouse-x)_*_100%)_calc(var(--mouse-y)_*_100%),_rgba(56,182,255,0.2)_0%,_transparent_70%)]" />
          </div>
          <div className="mb-12">
            <h1 className="text-3xl font-bold mb-8 text-center">Our Software Development Portfolio</h1>
            <TestimonialsSection />
          </div>
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4 justify-center">
              <PortfolioFilters onFilter={setFilters} />
              <PortfolioSearch onSearch={setSearchQuery} />
              <PortfolioSort 
                onSort={(value: 'newest' | 'oldest' | 'alphabetical') => setSortOption(value)} 
              />
            </div>
            {searchQuery && (
              <p className="text-sm text-black text-center">
                Showing {filteredProjects.length} projects matching "{searchQuery}"
              </p>
            )}
          </div>

          <FeaturedProjects />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Card 
                key={project.id}
                className="relative group overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
              >
                {project.previewImage && (
                  <div className="relative h-48 w-full overflow-hidden">
                    <img 
                      src={project.previewImage}
                      alt={project.name}
                      className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-0 opacity-100 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                      <h3 className="text-xl font-bold text-white">{project.name}</h3>
                      <p className="text-sm text-black">{project.client} • {project.year}</p>
                    </div>
                  </div>
                )}
                <CardContent className="p-6 transition-colors duration-300 group-hover:bg-black/10">
                  <p className="mb-4">{project.description}</p>
                  
                  {project.testimonial && (
                    <div className="mt-4 p-5 bg-white border border-black/10 rounded-lg shadow-sm">
                      {project.testimonial.avatar && (
                        <img 
                          src={project.testimonial.avatar} 
                          alt={`${project.testimonial.author}'s avatar`}
                          className="w-12 h-12 rounded-full mb-2 border border-black/10"
                        />
                      )}
                      <p className="text-sm italic text-black mb-2">"{project.testimonial.text}"</p>
                      <p className="text-xs font-medium text-black">{project.testimonial.author}</p>
                      <p className="text-xs text-black">{project.testimonial.position}</p>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-3 mt-4">
                    {project.technologies?.map(tech => (
                      <Badge key={tech} variant="secondary" className="text-xs">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                  
                  {project.metrics && (
                    <div className="grid grid-cols-3 gap-2 mt-4">
                      {project.metrics.map((metric: {value: string, description: string}, index: number) => (
                        <div key={index} className="bg-black/10 p-2 rounded text-center">
                          <p className="font-bold text-lg">{metric.value}</p>
                          <p className="text-xs text-black">{metric.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {project.impact && (
                    <div className="mt-4 pt-3 border-t border-black/20">
                      <p className="text-xs font-medium text-black mb-1">KEY IMPACT:</p>
                      <p className="text-sm text-black">{project.impact}</p>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="justify-between">
                  <CaseStudyForm projectName={project.name} />
                  {project.caseStudy && (
                    <div className="text-sm text-black hover:underline">
                      <a 
                        href={project.caseStudy} 
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Details →
                      </a>
                    </div>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>
          <section className="py-20">
            <div className="container mx-auto px-6">
              <WebDesignLocations />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
